/* Dash0 Real User Monitoring via the official web SDK (@dash0/sdk-web).
   Mirrors the bughop-fe integration: fully env-gated — with the env vars
   blank, nothing initializes and zero network calls are made.

   The SDK is OpenTelemetry-native and auto-captures Core Web Vitals, page
   views, HTTP requests, JS errors and user sessions over OTLP/HTTP.

   Env (injected at build time in .github/workflows/release.yml):
   - VITE_DASH0_ENDPOINT   — OTLP via HTTP endpoint URL for the org's region
   - VITE_DASH0_AUTH_TOKEN — ingest-only token scoped to the web dataset
     (public by nature of client monitoring; must be Ingesting-only)

   Sentry stays where it is (main.tsx) — this module is Dash0-only. */

import {
    init,
    identify,
    addSignalAttribute,
    removeSignalAttribute,
    sendEvent,
} from '@dash0/sdk-web'

const DASH0_ENDPOINT = (import.meta.env.VITE_DASH0_ENDPOINT ?? '').trim()
const DASH0_AUTH_TOKEN = (import.meta.env.VITE_DASH0_AUTH_TOKEN ?? '').trim()
// Optional — the token's default dataset applies when blank.
const DASH0_DATASET = (import.meta.env.VITE_DASH0_DATASET ?? '').trim()

const SERVICE_NAME = 'barber-mate-fe'

// Same build identity Sentry uses — release.yml passes the semver tag as
// VITE_BUILD_SHA so signals are filterable per release in Dash0.
const APP_VERSION = ((import.meta.env.VITE_BUILD_SHA as string | undefined) ?? '').trim() || 'dev'

// Whether Dash0 actually initialized — the helpers below no-op unless it
// did, so callers never have to know if telemetry is configured.
let dash0Ready = false

/** Initialize Dash0 RUM. Call as early as possible (main.tsx). */
export function initDash0(): void {
    if (!DASH0_ENDPOINT || !DASH0_AUTH_TOKEN) return
    init({
        serviceName: SERVICE_NAME,
        serviceVersion: APP_VERSION,
        environment: import.meta.env.MODE,
        endpoint: {
            url: DASH0_ENDPOINT,
            authToken: DASH0_AUTH_TOKEN,
            ...(DASH0_DATASET ? { dataset: DASH0_DATASET } : {}),
        },
    })
    dash0Ready = true
}

/* Structural user shape so this layer stays decoupled from domain models —
   both the barber CurrentUser and the customer Me satisfy it. */
export interface IdentifiedUser {
    id: string
    email?: string | null
    first_name?: string | null
    last_name?: string | null
}

/**
 * Associate the current session with a signed-in user. `kind` distinguishes
 * the two auth audiences; `shopId` slices barber signals per shop. Safe to
 * call repeatedly (runs on every context refresh).
 */
export function identifyDash0User(
    user: IdentifiedUser,
    kind: 'barber' | 'customer',
    shopId?: string,
): void {
    if (!dash0Ready) return
    identify(user.id, {
        email: user.email ?? undefined,
        fullName: [user.first_name, user.last_name].filter(Boolean).join(' ') || undefined,
    })
    addSignalAttribute('user.kind', kind)
    if (shopId) addSignalAttribute('barber_shop.id', shopId)
}

/** Drop the user association on logout so subsequent signals are anonymous. */
export function clearDash0Identity(): void {
    if (!dash0Ready) return
    removeSignalAttribute('user.kind')
    removeSignalAttribute('barber_shop.id')
    identify() // no id → clears the current user association
}

/** Emit a custom Dash0 event for a business action. No-ops when unconfigured. */
export function trackDash0Event(
    name: string,
    opts?: Parameters<typeof sendEvent>[1],
): void {
    if (dash0Ready) sendEvent(name, opts)
}
